import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 
  ((import.meta as any).env?.DEV ? 'http://localhost:3001/api/v1' : '/api/v1');

class ApiClient {
  private client: AxiosInstance;
  private pendingRequests = new Map<string, Promise<any>>();

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private createRequestKey(method: string, url: string, data?: any): string {
    return `${method}:${url}:${JSON.stringify(data)}`;
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token && token !== 'null' && token !== 'undefined') {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          // Don't try to refresh token for login/register endpoints as they don't require existing auth
          if (originalRequest.url?.includes('/auth?action=login') || 
              originalRequest.url?.includes('/auth?action=register') ||
              originalRequest.url?.includes('/auth/login') || 
              originalRequest.url?.includes('/auth/register')) {
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) {
              throw new Error('No refresh token available');
            }

            const response = await axios.post(`${API_BASE_URL}/auth?action=refresh`, {
              refreshToken
            });

            const { accessToken, refreshToken: newRefreshToken } = response.data.data;

            // Update stored tokens
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', newRefreshToken);

            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect to login (but not if we're already on login page)
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            
            // Only redirect if we're not already on the login or register page
            if (typeof window !== 'undefined' && 
                !window.location.pathname.includes('/login') && 
                !window.location.pathname.includes('/register')) {
              window.location.href = '/login';
            }
            
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    // Special deduplication for registration endpoints
    if (url.includes('/auth/register')) {
      const requestKey = this.createRequestKey('POST', url, data);
      
      if (this.pendingRequests.has(requestKey)) {
        return this.pendingRequests.get(requestKey)!;
      }
      
      const requestPromise = this.client.post<T>(url, data, config);
      this.pendingRequests.set(requestKey, requestPromise);
      
      // Clean up after request completes (success or failure)
      requestPromise.finally(() => {
        setTimeout(() => this.pendingRequests.delete(requestKey), 2000);
      });
      
      return requestPromise;
    }
    
    return this.client.post<T>(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config);
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, config);
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config);
  }

  // Special method for file uploads
  async uploadFiles<T = any>(
    url: string, 
    files: File[], 
    data?: Record<string, any>,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<AxiosResponse<T>> {
    const formData = new FormData();
    
    // Add files
    files.forEach((file, index) => {
      formData.append('images', file);
    });
    
    // Add other data
    if (data) {
      Object.keys(data).forEach(key => {
        if (data[key] !== undefined && data[key] !== null) {
          formData.append(key, data[key]);
        }
      });
    }

    return this.client.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
  }
}

export const apiClient = new ApiClient();